// src/components/campains/Campains.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import "./Campains.css";
import { Context } from "../../context/Context";
import { useAccess } from "../../access/hooks/useAccess";
import { useOperationalAccess } from "../../access/hooks/useOperationalAccess";
import SubscriptionLockNotice from "../subscription/SubscriptionLockNotice";
import { translations } from "../../utils/lang";
import { availableApps } from "../../utils/helpers";
import { normalizeIntegrationKey } from "../../utils/integrations";
import { CampainsCard } from "./CampainsCard";
import {
  addCampainAPI,
  fetchCampainAPI,
  updateCampainAPI,
} from "../../utils/api/campains";
import { useNavigate } from "react-router-dom";
import { Pencil, Plus, CircleX } from "lucide-react";
import { fetchStoresAPI } from "../../utils/api/operations";
import CatalogCampaignActions from "../operation/campaignActions/CatalogCampaignActions";
import { useCatalogRequests } from "../operation/campaignActions/useCatalogRequests";
import { Button, Card, Input, Select, SectionHeader, Checkbox } from "../ui";

// Promotional campaigns were removed; only these two types can be managed.
const CAMPAIGN_FIELDS = ["deliveryCamp", "paidCamp"];

const BASE_ENTRY = {
  app: "",
  paidCamp: [{ ar: "", en: "", dashboardVisablility: false }],
  deliveryCamp: [{ ar: "", en: "", dashboardVisablility: false }],
};

export const Campains = () => {
  const {
    lang,
    handleTokenExpiration,
    storeAccess,
  } = useContext(Context);
  const { can } = useAccess();
  const navigate = useNavigate();

  const [campains, setCampains] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const formRef = useRef(null);
  const [apps] = useState(availableApps);
  const [appFilter, setAppFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [newEntry, setNewEntry] = useState(BASE_ENTRY);
  const [visibilityLimitMessage, setVisibilityLimitMessage] = useState("");

  const canManageCampains = can("campaign.manage");
  // Restaurants the user can access (the server scopes this list), and the
  // Operations requests already made for each campaign.
  const [stores, setStores] = useState([]);
  // Same visibility rule as the Campaign Actions menu in Operations; the server decides what each role and restaurant is really
  // allowed. Requesting a campaign is operational: the role must hold it AND one of the client's stores must have an active
  // subscription; otherwise the action is shown locked and explained.
  const operational = useOperationalAccess(stores.map((store) => store.id));
  const campaignAccess = operational.check("campaign.request.create", undefined, { ignoreScope: true });
  const canRequestCampaigns = campaignAccess.allowed;
  const { byCampaign, reload: reloadRequests } = useCatalogRequests(true);
  useEffect(() => {
    const token = localStorage.getItem("auth-token");
    if (!token) return;
    fetchStoresAPI(token, storeAccess).then((data) => data && setStores(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Opens Operations on exactly the requests that were just handled.
  const viewInOperations = (actions) => {
    const ids = actions.map((action) => action._id).join(",");
    const storeIds = [...new Set(actions.map((action) => action.storeId))].join(",");
    navigate("/auth/operation?actions=" + encodeURIComponent(ids) + "&stores=" + encodeURIComponent(storeIds));
  };

  const getTypeLabel = (field) => {
    if (field === "deliveryCamp") return translations[lang].campains.header[1];
    return translations[lang].campains.header[2];
  };

  const allCards = campains.flatMap((c) =>
    CAMPAIGN_FIELDS.flatMap((field) =>
      (c[field] || []).map((val, i) => ({
        app: c.app,
        type: getTypeLabel(field),
        value: val,
        field,
        index: i,
        _id: c._id,
        fullData: c,
      })),
    ),
  );

  // Every real campaign, for the top-level Campaign Actions / Request Status.
  const actionCampaigns = allCards
    .filter((card) => card.value?._id)
    .map((card) => ({
      id: String(card.value._id),
      app: card.app,
      storeId: card.fullData?.storeId || null,
      type: card.field,
      title: (lang === "ar" ? card.value.ar || card.value.en : card.value.en || card.value.ar) || "",
      appLabel: translations[lang].appsNames[card.app] || card.app || "",
    }));

  const filteredCards = allCards.filter((card) => {
    const appOk = !appFilter || card.app === appFilter;
    const typeOk = !typeFilter || card.field === typeFilter;
    return appOk && typeOk;
  });

  const appOptions = apps.map((app, i) => ({
    value: app,
    label:
      translations[lang].appsNames[app] ||
      `${translations[lang].notApp} ${i + 1}`,
  }));

  const typeOptions = CAMPAIGN_FIELDS.map(
    (field) => ({
      value: field,
      label: getTypeLabel(field),
    }),
  );

  const formAppOptions = apps
    .filter(
      (app) => !campains.find((c) => c.app === app) || newEntry.app === app,
    )
    .map((app, i) => ({
      value: app,
      label:
        translations[lang].appsNames[app] ||
        `${translations[lang].notApp} ${i + 1}`,
    }));

  useEffect(() => {
    fetchCampains();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCampains = async () => {
    const token = localStorage.getItem("auth-token");
    if (token) {
      const data = await fetchCampainAPI(token);
      // Campaigns saved before the canonical vocabulary may hold a legacy
      // app spelling; compare and display them by canonical key.
      if (data)
        setCampains(
          data.map((campain) => ({
            ...campain,
            app: normalizeIntegrationKey(campain.app) || campain.app,
          })),
        );
    } else {
      handleTokenExpiration();
    }
  };

  const handleChange = (field, index, value, subField) => {
    if (CAMPAIGN_FIELDS.includes(field)) {
      setNewEntry((prev) => {
        const copy = [...prev[field]];
        const currentValue = copy[index]?.[subField];

        if (
          subField === "dashboardVisablility" &&
          value === true &&
          currentValue !== true
        ) {
          const visibleInSavedApp = campains
            .filter((campaign) => campaign.app === prev.app)
            .reduce(
              (count, campaign) =>
                count +
                CAMPAIGN_FIELDS.reduce(
                  (appCount, campaignType) =>
                    appCount +
                    (Array.isArray(campaign[campaignType])
                      ? campaign[campaignType].filter(
                          (item) => item?.dashboardVisablility === true,
                        ).length
                      : 0),
                  0,
                ),
              0,
            );

          const visibleInDraft = CAMPAIGN_FIELDS.reduce(
            (count, campaignType) =>
              count +
              (Array.isArray(prev[campaignType])
                ? prev[campaignType].filter(
                    (item) => item?.dashboardVisablility === true,
                  ).length
                : 0),
            0,
          );

          const totalAfterChange =
            maxCampDash.total - visibleInSavedApp + visibleInDraft + 1;

          if (totalAfterChange > maxCampDash.max) {
            setVisibilityLimitMessage(
              lang === "ar"
                ? "الحد الأقصى هو 6 حملات في لوحة التحكم. أزل حملة ظاهرة قبل إضافة أخرى."
                : "Maximum 6 dashboard campaigns allowed. Remove a visible campaign before adding another.",
            );
            return prev;
          }
        }

        copy[index] = { ...copy[index], [subField]: value };
        if (subField === "dashboardVisablility") {
          setVisibilityLimitMessage("");
        }
        return { ...prev, [field]: copy };
      });
    } else {
      setNewEntry((prev) => ({ ...prev, [field]: value }));
    }
  };

  const addTextArea = (field) => {
    setNewEntry((prev) => ({
      ...prev,
      [field]: [
        ...prev[field],
        { ar: "", en: "", dashboardVisablility: false },
      ],
    }));
  };

  const removeTextArea = (field, index) => {
    setNewEntry((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    if (!newEntry.app || !can("campaign.manage")) return;

    const token = localStorage.getItem("auth-token");
    const payload = {
      app: newEntry.app,
      deliveryCamp: newEntry.deliveryCamp.filter(Boolean),
      paidCamp: newEntry.paidCamp.filter(Boolean),
    };

    if (token) {
      const existing = campains.find((c) => c.app === newEntry.app);
      let success = null;
      if (existing) {
        success = await updateCampainAPI(token, existing._id, payload);
      } else {
        success = await addCampainAPI(token, payload);
      }

      if (success) {
        fetchCampains();
        setNewEntry(BASE_ENTRY);
        setShowAddForm(false);
      }
    } else {
      handleTokenExpiration();
    }
  };

  const handleUpdate = (card) => {
    setNewEntry({
      ...card.fullData,
      type: card.type,
      index: card.index,
      value: card.value,
    });
    setShowAddForm(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const maxCampDash = useMemo(() => {
    const MAX_VISIBLE = 6;

    let total = 0;

    const byApp = campains.map((campaign) => {
      const delivery = campaign.deliveryCamp ?? [];
      const paid = campaign.paidCamp ?? [];

      const deliveryVisible = delivery.filter(
        (camp) => camp.dashboardVisablility === true,
      ).length;

      const paidVisible = paid.filter(
        (camp) => camp.dashboardVisablility === true,
      ).length;

      const appTotal = deliveryVisible + paidVisible;

      total += appTotal;

      return {
        app: campaign.app,
        delivery: deliveryVisible,
        paid: paidVisible,
        total: appTotal,
      };
    });

    return {
      total,
      max: MAX_VISIBLE,
      remaining: Math.max(0, MAX_VISIBLE - total),
      reached: total >= MAX_VISIBLE,
      exceeded: total > MAX_VISIBLE,
      byApp,
    };
  }, [campains]);

  return (
    <div className="campains-container">
      {canManageCampains && (
        <Card
          className="campains-filter-card campains-visibility"
          hoverable={false}
          padding="sm"
        >
          <div className="campains-visibility-summary">
            <strong className="campains-visibility-title">
              {lang === "ar" ? "الظهور في لوحة التحكم" : "Dashboard Visibility"}
            </strong>
            <span className="campains-visibility-dot" aria-hidden="true">
              •
            </span>
            <span className="campains-visibility-count">
              <strong>
                {maxCampDash.total} / {maxCampDash.max}
              </strong>{" "}
              {lang === "ar" ? "ظاهرة" : "Visible"}
            </span>
            <span
              className={`campains-visibility-badge ${
                maxCampDash.reached ? "is-reached" : ""
              }`}
            >
              {maxCampDash.reached
                ? lang === "ar"
                  ? "تم الوصول إلى الحد الأقصى"
                  : "Maximum Reached"
                : `${maxCampDash.remaining} ${
                    lang === "ar" ? "أماكن متبقية" : "Slots Remaining"
                  }`}
            </span>
          </div>

          <div
            className="campains-visibility-track"
            role="progressbar"
            aria-label={
              lang === "ar" ? "الظهور في لوحة التحكم" : "Dashboard visibility"
            }
            aria-valuemin="0"
            aria-valuemax={maxCampDash.max}
            aria-valuenow={Math.min(maxCampDash.total, maxCampDash.max)}
          >
            <div
              className={`campains-visibility-fill ${
                maxCampDash.reached ? "is-reached" : ""
              }`}
              style={{
                width: `${Math.min(
                  100,
                  (maxCampDash.total / maxCampDash.max) * 100,
                )}%`,
              }}
            />
          </div>

          {maxCampDash.byApp.length > 0 && (
            <div className="campains-visibility-apps">
              <span className="campains-visibility-apps-label">
                {lang === "ar" ? "حسب التطبيق:" : "By App:"}
              </span>
              {maxCampDash.byApp.map(({ app, total }, index) => (
                <span
                  key={`${app || "app"}-${index}`}
                  className="campains-visibility-app"
                >
                  <span>
                    {translations[lang].appsNames[app] ||
                      app ||
                      `${translations[lang].notApp} ${index + 1}`}
                  </span>
                  <strong>{total}</strong>
                </span>
              ))}
            </div>
          )}

          {visibilityLimitMessage && (
            <div className="campains-visibility-alert" role="alert">
              <span aria-hidden="true">⚠ </span>
              {visibilityLimitMessage}
            </div>
          )}
        </Card>
      )}

      <Card className="campains-filter-card" hoverable={false}>
        <div className="campains-filter">
          <Select
            value={appFilter}
            options={appOptions}
            placeholder={translations[lang].requests.dropdownTitle[2]}
            onChange={(e) => setAppFilter(e.target.value)}
          />

          <Select
            value={typeFilter}
            options={typeOptions}
            placeholder={translations[lang].campains.chose}
            onChange={(e) => setTypeFilter(e.target.value)}
          />

          <Button
            variant="danger"
            appearance="soft"
            fullWidth={false}
            onClick={() => {
              setAppFilter("");
              setTypeFilter("");
            }}
          >
            {lang === "ar" ? "مسح الفلاتر" : "Clear Filters"}
          </Button>
        </div>
      </Card>

      {campaignAccess.locked && (
        <SubscriptionLockNotice
          lang={lang}
          reason={campaignAccess.reason}
          title={lang === "ar" ? "طلبات الحملات" : "Campaign requests"}
        />
      )}

        <CatalogCampaignActions
          campaigns={actionCampaigns}
          byCampaign={byCampaign}
          stores={stores}
          canAct={canRequestCampaigns}
          onChanged={reloadRequests}
          onViewInOperations={viewInOperations}
        />

      <div className="campains-cards-container">
        {filteredCards.map((card, i) => (
          <CampainsCard
            key={`${card.app}-${card.field}-${i}`}
            {...card}
            onUpdate={() => handleUpdate(card)}
          />
        ))}
      </div>

      {showAddForm && (
        <div ref={formRef}>
          <Card className="campains-form" hoverable={false}>
            <SectionHeader
              title={
                translations[lang].campains.title ||
                translations[lang].campains.chose
              }
            />

            <Select
              value={newEntry.app}
              options={formAppOptions}
              placeholder={translations[lang].requests.dropdownTitle[2]}
              onChange={(e) => handleChange("app", null, e.target.value)}
            />

            <div className="campains-fields">
              {CAMPAIGN_FIELDS.map((field, fi) => (
                <div key={field} className="campains-field">
                  <h4>{translations[lang].campains.header[fi + 1]}</h4>

                  {newEntry[field].map((txt, idx) => (
                    <div key={idx} className="campains-textarea-box">
                      <div className="campains-textarea-header">
                        <span>
                          {translations[lang].campains.header[fi + 1]} -{" "}
                          {idx + 1}
                        </span>

                        <Button
                          variant="danger"
                          appearance="ghost"
                          icon={<CircleX size={18} />}
                          title={translations[lang].financial.delete}
                          onClick={() => removeTextArea(field, idx)}
                        />
                      </div>

                      <div className="campains-textarea-row">
                        <Input
                          textarea
                          rows="3"
                          placeholder="AR"
                          value={txt.ar}
                          onChange={(e) =>
                            handleChange(field, idx, e.target.value, "ar")
                          }
                        />

                        <Input
                          textarea
                          rows="3"
                          placeholder="EN"
                          value={txt.en}
                          onChange={(e) =>
                            handleChange(field, idx, e.target.value, "en")
                          }
                        />
                        <Checkbox
                          checked={!!txt.dashboardVisablility}
                          onChange={(e) =>
                            handleChange(
                              field,
                              idx,
                              e.target.checked,
                              "dashboardVisablility",
                            )
                          }
                          label={
                            lang === "ar"
                              ? "رؤيته في لوحتة التحكم"
                              : "Dashboard Vis"
                          }
                        />
                      </div>
                    </div>
                  ))}

                  <Button
                    variant="primary"
                    appearance="soft"
                    size="sm"
                    icon={<Plus size={16} />}
                    onClick={() => addTextArea(field)}
                  >
                    {translations[lang].notifications.add}
                  </Button>
                </div>
              ))}
            </div>

            <div className="campains-form-actions">
              <Button
                variant="primary"
                appearance="filled"
                onClick={handleSubmit}
              >
                {translations[lang].requests.submit}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {canManageCampains && (
        <Button
          className="campains-add-btn"
          variant="primary"
          appearance="soft"
          size="sm"
          rounded
          icon={showAddForm ? <CircleX size={18} /> : <Pencil size={18} />}
          onClick={() => setShowAddForm((prev) => !prev)}
          title={
            showAddForm
              ? translations[lang].financial.cancel
              : translations[lang].financial.update
          }
        />
      )}
    </div>
  );
};
